/**
 * THE VOICE ORBS ("Add orbs that follow the voices" — VisualHost.setOrb / pushOrbPcm / resetOrb, engineApi 1.23).
 * One control per render loop pairs the host's orb system with its audio: the stereo blocks come from the ONE tap
 * the hearing already hangs off the source (audio/hearing.ts hearOrb), and every jump in the audio (a new source,
 * a new track, a seek) resets them there. WHAT the orbs look like, and WHETHER a surface draws them at all, is
 * renderPolicy orbFor's — a TV surface resolves to disabled whatever it is asked (owner, 2026-10-30: orbs are for
 * the screen in the user's hand and never reach a TV).
 */
import type { VisualHost } from '@syqel/engine'
import { hearOrb, type OrbSink } from './audio/hearing'
import { CAPABLE_DEVICE, orbFor, type OrbDevice, type Surface } from './renderPolicy'

type OrbHost = Pick<VisualHost, 'setOrb' | 'pushOrbPcm' | 'resetOrb'>

export interface OrbStats {
  /** the host's orbs are on (what setOrb was last told) */
  on: boolean
  /** they follow the voice's pitch + pan — fed the tap's stereo blocks (renderPolicy orbFor followsPitch) */
  pcm: boolean
  /** stereo blocks handed to pushOrbPcm, and orb resets, since the control was made */
  blocks: number
  resets: number
}

export interface OrbControl {
  /** Each frame (or on a change): does this surface's user want the orbs? Only a change does anything. */
  apply(wants: boolean): void
  /** for the perf probe — what this loop's orbs are doing */
  stats(): OrbStats
  dispose(): void
}

export function createOrbControl(host: Partial<OrbHost>, surface: Surface, device: OrbDevice = CAPABLE_DEVICE): OrbControl {
  // an OTA engine-core older than engineApi 1.23 has no orb system: the toggle then does nothing
  const h = typeof host.setOrb === 'function' ? (host as OrbHost) : null
  let blocks = 0
  let resets = 0
  const sink: OrbSink | null = h ? {
    push: (l, r, rate) => { blocks += 1; h.pushOrbPcm(l, r, rate) },
    reset: () => { resets += 1; h.resetOrb() }
  } : null
  let on: boolean | null = null
  let pcm = false
  return {
    apply(wants) {
      const opts = orbFor(surface, wants, device)
      if (opts.enabled === on) return
      on = opts.enabled
      pcm = on && opts.followsPitch === true
      h?.setOrb({ enabled: opts.enabled, size: opts.size, count: opts.count })
      if (on) sink?.reset() // a fresh start, not the tails of whatever it followed when last on
      hearOrb(on ? sink : null, pcm)
    },
    stats: () => ({ on: on === true, pcm, blocks, resets }),
    dispose() {
      if (on) hearOrb(null)
      on = null
    }
  }
}
